import { useEffect, useRef, useState } from 'react';
import { useSocket } from 'socket.io-react-hook';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { _getToken } from 'src/Config/axiosApi';
import { refreshAuthToken } from 'src/Common/context/AuthContext';
import type {
  MatchEventFrame,
  MatchOperation,
} from '../types/matchEvents.types';
import type { MatchState, MatchStatePayload } from '../types/sniper.types';
import { matchEventStateQueryKey } from '../api/useMatchEvents';
import { compareStreamIds } from '../utils/notificationsFeed';
import { MAX_MATCH_EVENT_ROWS, parseTsToMillis } from '../utils/matchEventsFeed';
import {
  operationKey,
  operationStableId,
  boundSeenOps,
  reconcileOperations,
} from '../utils/matchOperationsFeed';

/**
 * Seconds until a JWT's `exp`, or null if it can't be decoded. Local
 * copy (AuthContext's helper is not exported) — only reads `exp`. Mirror
 * of the same helper in `useNotificationsSocket`.
 */
function secondsUntilTokenExpiry(token: string | null): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { exp?: number };
    if (!payload?.exp) return null;
    return payload.exp - Math.floor(Date.now() / 1000);
  } catch {
    return null;
  }
}

/** Refresh the token this many seconds BEFORE it expires. */
const PROACTIVE_REFRESH_LEAD_SECONDS = 60;

/** Cooldown between auto-refresh attempts on a connect/auth error. */
const AUTH_RECOVERY_COOLDOWN_MS = 10_000;

/** The WS namespace for the live match-events feed (frozen contract §WS). */
export const MATCH_EVENTS_NAMESPACE = '/match-events';

/**
 * Upper bound on the seen-operation-key set. Operations carry no streamId, so a
 * resubscribe re-delivery is de-duped by remembering keys — but an unbounded Set
 * grows for the whole session. We keep well more than the visible buffer
 * ({@link MAX_MATCH_EVENT_ROWS}) so redelivery within the retained window still
 * de-dupes, and evict the oldest keys once the cap is exceeded.
 */
export const MAX_SEEN_OPS = MAX_MATCH_EVENT_ROWS * 4;

/**
 * Resolve the namespaced socket URL. The feed shares the notifications-
 * websocket base (`VITE_NOTIFICATIONS_WS_URL`) and lives on the
 * `/match-events` socket.io namespace, so we append the namespace path
 * to the base URL (socket.io routes the trailing path to the namespace).
 * A dedicated `VITE_MATCH_EVENTS_WS_URL` overrides the base if the feed
 * is ever split onto its own host (mirrors the `VITE_LIVE_PREDICTIONS_WS_URL`
 * fallback precedent).
 */
export function resolveMatchEventsWsUrl(): string {
  const base =
    import.meta.env.VITE_MATCH_EVENTS_WS_URL ||
    import.meta.env.VITE_NOTIFICATIONS_WS_URL ||
    '';
  if (!base) return '';
  return `${base.replace(/\/+$/, '')}${MATCH_EVENTS_NAMESPACE}`;
}

/** The slice of the socket the room registry needs. */
interface RoomSocket {
  emit: (event: string, payload: unknown) => void;
  on: (event: string, cb: () => void) => void;
}

/**
 * Per-socket `subscribe_match` ref count.
 *
 * `IoProvider` already ref-counts the CONNECTION per namespace; this does the
 * same one level down, for the ROOMS. Several hook instances legitimately want
 * the same fixture at once — the Sniper page's signal hook plus one card per
 * match — and `subscribe_match` is not free: the gateway answers EVERY one by
 * replaying up to 500 durable `match_operations`. One emit per interested hook
 * meant that replay crossed the wire once per holder, and again on every
 * reconnect.
 *
 * Rooms are socket-level, so one join serves every listener: the gateway's
 * backfill/replay are `client.emit`s to the SOCKET, not room broadcasts. Each
 * hook instance keeps its own per-instance fixture filter unchanged, so nothing
 * starts seeing fixtures it did not ask for — only the wire chatter collapses.
 *
 * `holders` is who wants each room; `wireJoined` is what the SERVER currently
 * believes, which a reconnect resets (see `roomRegistry`).
 */
interface RoomRegistry {
  holders: Map<number, Set<number>>;
  wireJoined: Set<number>;
  /** Fixtures the server is currently streaming the raw firehose for. */
  wireIncludeRaw: Set<number>;
}

/** Keyed by socket so each namespace — and each test — gets a fresh registry. */
const roomRegistries = new WeakMap<object, RoomRegistry>();
let nextHolderId = 1;

function roomRegistry(socket: RoomSocket): RoomRegistry {
  const existing = roomRegistries.get(socket);
  if (existing) return existing;
  const registry: RoomRegistry = {
    holders: new Map(),
    wireJoined: new Set(),
    wireIncludeRaw: new Set(),
  };
  roomRegistries.set(socket, registry);
  // A (re)connect wipes the server's room membership, so forget what we think
  // it knows. Ordering is load-bearing: the subscribe effect calls
  // `roomRegistry(socket)` at its very top, so this listener is registered
  // before ANY instance's `resubscribeAll` and the reset always runs first. It
  // outlives every hook instance on purpose — the registry is the socket's, not
  // an instance's.
  socket.on('connect', () => {
    registry.wireJoined.clear();
    registry.wireIncludeRaw.clear();
  });
  return registry;
}

/**
 * Declare that `holderId` wants `fixtureId`, emitting `subscribe_match` only
 * when the server does not already have us in the room.
 *
 * Two things force an emit even when the room is already joined, because the
 * join someone else made does not give this holder what it needs:
 *   - a replay `cursor` — `subscribe_match` is what triggers its `XRANGE`, and
 *     an already-joined room delivers live frames but not the missed history;
 *   - `includeRaw` — the server only starts the `player_stat`/`timer` firehose
 *     on a subscribe that asks for it, so a raw holder joining behind a
 *     non-raw one would silently never see the firehose.
 * Only `trackFrames` callers hold a cursor and only the live hub asks for raw,
 * so on a page of plain holders this still collapses to one emit per fixture.
 */
function joinRoom(
  socket: RoomSocket,
  holderId: number,
  fixtureId: number,
  cursor: string | undefined,
  includeRaw: boolean,
): void {
  const registry = roomRegistry(socket);
  let holders = registry.holders.get(fixtureId);
  if (!holders) {
    holders = new Set();
    registry.holders.set(fixtureId, holders);
  }
  holders.add(holderId);
  const needsRaw = includeRaw && !registry.wireIncludeRaw.has(fixtureId);
  if (!cursor && !needsRaw && registry.wireJoined.has(fixtureId)) return;
  registry.wireJoined.add(fixtureId);
  if (includeRaw) registry.wireIncludeRaw.add(fixtureId);
  socket.emit('subscribe_match', {
    fixtureId,
    ...(cursor ? { lastStreamId: cursor } : {}),
    includeRaw,
  });
}

/**
 * Drop `holderId`'s claim on `fixtureId`, emitting `unsubscribe_match` only on
 * the last-holder-out transition. This is what keeps a card unmounting (or
 * strict-mode remounting) from pulling the room out from under the page hook.
 */
function leaveRoom(
  socket: RoomSocket,
  holderId: number,
  fixtureId: number,
): void {
  const registry = roomRegistry(socket);
  const holders = registry.holders.get(fixtureId);
  holders?.delete(holderId);
  if (holders && holders.size > 0) return;
  registry.holders.delete(fixtureId);
  registry.wireIncludeRaw.delete(fixtureId);
  // Never emit an unsubscribe for a room the server no longer has us in.
  if (!registry.wireJoined.delete(fixtureId)) return;
  socket.emit('unsubscribe_match', { fixtureId });
}

/**
 * Record that the server's raw-firehose flag for `fixtureIds` is now `includeRaw`.
 * For the `includeRaw` TOGGLE, which re-subscribes rooms it already holds and so
 * bypasses {@link joinRoom} — without this the registry would keep answering
 * with the pre-toggle flag and silence the next raw holder to join.
 */
function setWireIncludeRaw(
  socket: RoomSocket,
  fixtureIds: Iterable<number>,
  includeRaw: boolean,
): void {
  const registry = roomRegistry(socket);
  for (const fixtureId of fixtureIds) {
    if (includeRaw) registry.wireIncludeRaw.add(fixtureId);
    else registry.wireIncludeRaw.delete(fixtureId);
  }
}

/**
 * Drop every claim `holderId` holds WITHOUT emitting — for the reconnect path,
 * where the server has already forgotten the rooms. Without this, a fixture an
 * instance stopped wanting while disconnected would keep its claim alive
 * forever and the room would never be released.
 */
function releaseHolder(socket: RoomSocket, holderId: number): void {
  const registry = roomRegistry(socket);
  for (const [fixtureId, holders] of registry.holders) {
    holders.delete(holderId);
    if (holders.size === 0) registry.holders.delete(fixtureId);
  }
}

export interface UseMatchEventsSocketOptions {
  /** The fixtures to subscribe to (the live hub's ACTIVE set). */
  fixtureIds: number[];
  /** Request the raw firehose (`player_stat` + `timer`) for every fixture. */
  includeRaw?: boolean;
  /**
   * Buffer incoming `match_event` frames into `frames` (default true). A
   * caller that only reads `matchState` (e.g. one `SniperEventCard` per live
   * match) has no use for the raw incident list — with this off, an incoming
   * frame for a subscribed fixture is dropped after the replay-cursor check
   * instead of growing a `MAX_MATCH_EVENT_ROWS`-capped array and re-rendering
   * on every single incident. Real Sniper groups run up to ~30 concurrent
   * event cards; each uselessly buffering up to 5000 frames (and re-rendering
   * on every one) is real, measured memory/CPU pressure, not a hypothetical.
   */
  trackFrames?: boolean;
  /** Same tradeoff as {@link trackFrames}, for `match_operation`s. */
  trackOperations?: boolean;
  /**
   * OBSERVE every incoming `match_event` for a subscribed fixture without
   * buffering it. Runs whatever `trackFrames` is — a caller that only needs to
   * REDUCE frames (e.g. "when did this fixture last signal a goal") gets the
   * signal with `trackFrames: false`, so nothing is accumulated and no render
   * happens unless the reduction itself changes.
   *
   * Called for replays too (the per-fixture cursor de-dupe only guards the
   * BUFFER), so an observer must be idempotent — reduce on the frame's own
   * `receivedAt`, never on `Date.now()`.
   *
   * Read through a ref: changing the callback identity never re-subscribes.
   */
  onFrame?: (frame: MatchEventFrame) => void;
  /** Same contract as {@link onFrame}, for `match_operation`s. */
  onOperation?: (op: MatchOperation) => void;
  /**
   * Open the connection at all (default true). A caller with nothing to watch —
   * e.g. the Sniper page while the groups LANDING is open, before any group is
   * selected — passes false so the page does not hold a `/match-events` socket
   * it has no fixtures for.
   */
  enabled?: boolean;
}

/** The latest backend `MatchState` for a fixture plus its emit timestamp. */
export interface MatchStateEntry {
  state: MatchState;
  ts: string;
}

export interface MatchEventsSocketApi {
  isConnected: boolean;
  /** Buffered incident frames across ALL subscribed fixtures (append order). */
  frames: MatchEventFrame[];
  /** Buffered `match_operation`s across ALL subscribed fixtures (append order). */
  operations: MatchOperation[];
  /**
   * Latest backend-authored `MatchState` per fixture (the `match_state` event).
   * The FE RENDERS this — score/corners/ARMED/VAR are never FE-derived.
   */
  matchState: Map<number, MatchStateEntry>;
}

/**
 * Connects to the notifications-websocket service on the `/match-events`
 * namespace and streams the live hub: `match_event` incident frames AND
 * `match_operation` sniper/manual actions for EVERY active fixture.
 *
 * Subscription model (frozen contract §WS `/match-events`):
 *  - The desired set = `fixtureIds`. On (re)connect we (re)subscribe the whole
 *    set (the server forgets our rooms across a reconnect); while connected we
 *    diff the set and `subscribe_match` newcomers / `unsubscribe_match` leavers.
 *  - Incidents arrive on `match_event`, operations on `match_operation`; each
 *    incident carries a per-fixture `streamId` cursor so a resubscribe replays
 *    via `XRANGE` with no gap. Operations carry no cursor and are de-duped by a
 *    composite {@link operationKey}.
 *
 * Auth / resilience: reuses the notifications-socket pattern VERBATIM —
 * socket.io owns an infinite, backed-off reconnect loop; we proactively
 * refresh the JWT before `exp`; on `connect_error`/`auth_error` we
 * refresh-and-retry (once per cooldown) without ever tearing the socket
 * down or logging the operator out (`clearOnFailure: false`).
 */
export function useMatchEventsSocket(
  options: UseMatchEventsSocketOptions,
): MatchEventsSocketApi {
  const {
    fixtureIds,
    includeRaw = false,
    trackFrames = true,
    trackOperations = true,
    onFrame,
    onOperation,
    enabled = true,
  } = options;
  // Read via refs, not the destructured values, in the intake effect below —
  // that effect's deps are just `[socket]` (frame/operation ORDER must never
  // reorder from a resubscribe), so a caller flipping these flags at runtime
  // must not require a re-subscribe to take effect.
  const trackFramesRef = useRef(trackFrames);
  trackFramesRef.current = trackFrames;
  const trackOperationsRef = useRef(trackOperations);
  trackOperationsRef.current = trackOperations;
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;
  const onOperationRef = useRef(onOperation);
  onOperationRef.current = onOperation;
  const wsUrl = resolveMatchEventsWsUrl();
  const accessToken = _getToken();
  const queryClient = useQueryClient();
  // Stable identity of THIS hook instance in the socket's room ref count.
  // Lazy `useState` initialiser so it is allocated exactly once per instance
  // and survives every re-render (a `useRef(nextHolderId++)` would burn an id
  // on every render).
  const [holderId] = useState(() => nextHolderId++);

  const { socket } = useSocket(wsUrl || '', {
    enabled: enabled && !!wsUrl && !!accessToken,
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 30_000,
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() }),
  });

  const [frames, setFrames] = useState<MatchEventFrame[]>([]);
  const [operations, setOperations] = useState<MatchOperation[]>([]);
  const [matchState, setMatchState] = useState<Map<number, MatchStateEntry>>(
    () => new Map(),
  );

  // Per-fixture replay cursor — id of the most-recently-applied stream entry
  // for each fixture. Empty means "never applied one" so its first subscribe
  // omits the cursor.
  const cursorsRef = useRef<Map<number, string>>(new Map());
  // Seen operation keys — operations carry no streamId, so a resubscribe
  // re-delivery is de-duped by composite key.
  const seenOpsRef = useRef<Set<string>>(new Set());

  // The currently-subscribed fixtures — so a set change can leave the rooms
  // that dropped out before joining the new ones.
  const subscribedRef = useRef<Set<number>>(new Set());
  // Latest desired set + includeRaw, read by the (stable) subscribe closure.
  const desiredRef = useRef<number[]>(fixtureIds);
  desiredRef.current = fixtureIds;
  const includeRawRef = useRef<boolean>(includeRaw);
  includeRawRef.current = includeRaw;

  const lastAuthRecoveryAt = useRef(0);

  // A stable dependency for the reconcile effect (the array identity churns).
  const fixtureIdsKey = [...fixtureIds].sort((a, b) => a - b).join(',');

  // ── Frame + operation intake ────────────────────────────────────────────
  useEffect(() => {
    // Read the subscribed set fresh on each event — the reconcile effect
    // swaps the Set object on reconnect, so capturing it here would go stale.
    const onMatchEvent = (frame: MatchEventFrame) => {
      const subscribed = subscribedRef.current;
      // Ignore stray frames for a fixture we're no longer subscribed to.
      if (subscribed.size > 0 && !subscribed.has(frame.fixtureId)) return;
      // Non-buffering observers run FIRST and unconditionally — a reducer
      // caller (`trackFrames: false`) must still see every frame.
      onFrameRef.current?.(frame);
      // A caller that only reads `matchState` (see `trackFrames` doc) has no
      // use for the buffer — skip the cursor bookkeeping and the array grow
      // + re-render entirely, rather than pay for a `MAX_MATCH_EVENT_ROWS`
      // buffer nothing ever reads.
      if (!trackFramesRef.current) return;
      const incomingId = frame.streamId;
      if (incomingId) {
        const cursor = cursorsRef.current.get(frame.fixtureId) ?? '';
        const cmp = cursor ? compareStreamIds(incomingId, cursor) : 1;
        // Already-applied stream id for this fixture — a replay re-delivery.
        if (cmp <= 0) return;
        cursorsRef.current.set(frame.fixtureId, incomingId);
      }
      setFrames((prev) => {
        const next = [...prev, frame];
        return next.length > MAX_MATCH_EVENT_ROWS
          ? next.slice(next.length - MAX_MATCH_EVENT_ROWS)
          : next;
      });
    };

    const onMatchOperation = (op: MatchOperation) => {
      const subscribed = subscribedRef.current;
      if (subscribed.size > 0 && !subscribed.has(op.fixtureId)) return;
      // De-dupe BEFORE the observers, and do it whatever `trackOperations` says.
      // The gateway backfills the durable `match_operations` on EVERY
      // `subscribe_match` (not just a cursor replay), so when two hook instances
      // on the shared socket both subscribe a fixture, every stored op is
      // delivered TWICE. Observers are documented as idempotent, but handing
      // them avoidable duplicates makes that contract carry weight it shouldn't.
      // The Set is bounded, so running it for observer-only callers is cheap.
      // The composite key includes `ts`, so an EXACT re-delivery (resubscribe
      // replay) de-dupes here, while a sniper fill re-emit (new `ts`, `filled`
      // climbs 0 → 78) passes through to be reconciled onto its existing row by
      // stable id — a fill-update is an in-place UPDATE, never a duplicate row.
      // A sniper fill re-emit reconciles by its stable id and `reconcileOperations`
      // is idempotent (monotonic `filled`), so it must NOT be gated by the
      // ts-based seen-set — two fills folded/emitted in the same millisecond
      // would otherwise share a key and the second update would be dropped.
      // Ask `operationStableId` — never re-spell its namespace literal here.
      // It returns non-null ONLY for a collapsing key, so this IS the same
      // question; re-testing the prefix meant that if the relay ever renamed
      // the namespace, `operationStableId` would keep working through its one
      // constant while this line silently went false, dropping re-emits into
      // the ts-keyed seen-set — the exact loss the comment above warns about.
      const isSniperReemit = operationStableId(op) != null;
      if (!isSniperReemit) {
        const key = operationKey(op);
        const seen = seenOpsRef.current;
        if (seen.has(key)) return;
        seen.add(key);
        // Bound the de-dupe set so a long session doesn't leak (evicts oldest).
        boundSeenOps(seen, MAX_SEEN_OPS);
      }
      onOperationRef.current?.(op);
      // See `trackOperations` — skip the buffer + re-render entirely for a
      // caller that never reads `operations`.
      if (!trackOperationsRef.current) return;
      // Per-fixture op cap: not applied here — the shared MAX_MATCH_EVENT_ROWS
      // buffer is a flat append list, so a per-fixture quota would require
      // grouping on every intake. Left out intentionally (noisy-match starvation
      // is bounded by the row cap; revisit only if a real starvation shows up).
      setOperations((prev) =>
        reconcileOperations(prev, op, MAX_MATCH_EVENT_ROWS),
      );
    };

    // Backend-authored MatchState (score/corners/clock/VAR). The FE renders it
    // verbatim; a late/out-of-order push must not clobber a newer reading. `ts`
    // is ISO-8601 UTC — ordered numerically via `parseTsToMillis` (never string
    // `<`, which only holds for a fixed-width canonical form). A missing/garbled
    // `ts` sorts lowest so it can never shadow a good existing value.
    const onMatchState = (payload: MatchStatePayload) => {
      if (!payload || payload.state == null) return;
      const subscribed = subscribedRef.current;
      if (subscribed.size > 0 && !subscribed.has(payload.fixtureId)) return;
      setMatchState((prev) => {
        const existing = prev.get(payload.fixtureId);
        if (
          existing &&
          parseTsToMillis(payload.ts) < parseTsToMillis(existing.ts)
        ) {
          return prev;
        }
        const next = new Map(prev);
        next.set(payload.fixtureId, { state: payload.state, ts: payload.ts });
        return next;
      });
    };

    socket.on('match_event', onMatchEvent);
    socket.on('match_operation', onMatchOperation);
    socket.on('match_state', onMatchState);
    return () => {
      socket.off('match_event', onMatchEvent);
      socket.off('match_operation', onMatchOperation);
      socket.off('match_state', onMatchState);
    };
  }, [socket]);

  // ── (Re)subscribe: reconcile the desired set with the joined rooms ──────
  useEffect(() => {
    // Materialise the socket's room registry FIRST. Creating it also registers
    // the `connect` listener that forgets the server's room state, and that
    // reset MUST run ahead of every instance's `resubscribeAll` — including
    // this one's, registered a few lines below. Leaving it to be created lazily
    // by the first `joinRoom` put it AFTER this instance's handler, so on a
    // reconnect the first holder re-subscribed against stale wire state, found
    // its rooms "already joined", and stayed silent.
    roomRegistry(socket);

    const emitSubscribe = (fixtureId: number) => {
      joinRoom(
        socket,
        holderId,
        fixtureId,
        cursorsRef.current.get(fixtureId),
        includeRawRef.current,
      );
    };

    // Full (re)subscribe — the server forgot our rooms across the (re)connect.
    const resubscribeAll = () => {
      subscribedRef.current = new Set();
      // Hand this instance's room claims back before re-declaring them, so a
      // fixture it stopped wanting while disconnected does not keep the room
      // pinned. Quiet by design — the server already dropped every room, and
      // the registry's own `connect` listener has already cleared the wire
      // state, so the first holder to reconcile re-emits and the rest ride it.
      releaseHolder(socket, holderId);
      // `match_event` frames replay via the per-fixture XRANGE cursor, but
      // `match_state` has NO WS replay: a score change during the outage would
      // otherwise be lost until the next live incident. Re-backfill the REST
      // state snapshot for every desired fixture so the current state is
      // recovered on reconnect; the fresher of {REST, live} then wins.
      for (const fx of desiredRef.current) {
        queryClient.invalidateQueries(matchEventStateQueryKey(fx));
      }
      reconcile();
    };

    // Drop the cached `match_state` for a fixture we just left so the Map does
    // not grow unbounded across a long session of rotating active fixtures.
    const pruneMatchState = (fixtureId: number) => {
      setMatchState((prev) => {
        if (!prev.has(fixtureId)) return prev;
        const next = new Map(prev);
        next.delete(fixtureId);
        return next;
      });
    };

    // Diff the desired set against the joined rooms; join newcomers, leave
    // fixtures that dropped out of the active set.
    const reconcile = () => {
      const desired = new Set(desiredRef.current);
      const subscribed = subscribedRef.current;
      for (const fx of subscribed) {
        if (!desired.has(fx)) {
          leaveRoom(socket, holderId, fx);
          subscribed.delete(fx);
          pruneMatchState(fx);
        }
      }
      for (const fx of desired) {
        if (!subscribed.has(fx)) {
          emitSubscribe(fx);
          subscribed.add(fx);
        }
      }
    };

    socket.on('connect', resubscribeAll);
    if (socket.connected) reconcile();

    return () => {
      socket.off('connect', resubscribeAll);
    };
  }, [socket, socket.connected, fixtureIdsKey, queryClient, holderId]);

  // ── includeRaw toggle: force a FULL resubscribe of the active set ─────────
  // The reconcile effect keys on the fixture SET only (includeRaw is read from a
  // ref), so flipping "Raw / all incidents" while connected with a stable set
  // would be a silent no-op — the server would never start/stop the
  // player_stat+timer firehose. Re-emit subscribe_match for every joined fixture
  // with the new includeRaw. Skip the first run (the initial subscribe is owned
  // by the reconcile effect) to avoid a double subscribe on mount; while
  // disconnected we skip too — the connect handler resubscribes from the fresh
  // ref, so there's no gap.
  const includeRawInitialisedRef = useRef(false);
  useEffect(() => {
    if (!includeRawInitialisedRef.current) {
      includeRawInitialisedRef.current = true;
      return;
    }
    if (!socket.connected) return;
    // Deliberately bypasses the room ref count: this is not a join, it is a
    // re-subscribe of rooms we already hold, purely to flip the server-side raw
    // flag. It must reach the server even though the room is joined — but the
    // registry's view of the raw state has to follow, or a later joiner would
    // read a stale flag.
    setWireIncludeRaw(socket, subscribedRef.current, includeRaw);
    for (const fx of subscribedRef.current) {
      const cursor = cursorsRef.current.get(fx);
      socket.emit('subscribe_match', {
        fixtureId: fx,
        ...(cursor ? { lastStreamId: cursor } : {}),
        includeRaw,
      });
    }
  }, [socket, includeRaw]);

  // ── Release every room claim on unmount ─────────────────────────────────
  // Pairs with the `joinRoom` in the reconcile effect: this is the ONLY place a
  // full teardown decrements, so a strict-mode (or route-churn) remount hands
  // the claims back and immediately re-takes them. `unsubscribe_match` goes out
  // only if this instance was the last holder — a card unmounting can never
  // pull the room out from under the page-level signal hook.
  useEffect(() => {
    return () => {
      for (const fx of subscribedRef.current) {
        try {
          leaveRoom(socket, holderId, fx);
        } catch {
          /* socket already torn down */
        }
      }
      subscribedRef.current = new Set();
    };
  }, [socket, holderId]);

  // ── Proactive token refresh before `exp` ────────────────────────────────
  useEffect(() => {
    const ttl = secondsUntilTokenExpiry(accessToken);
    if (ttl === null) return;
    const delayMs = Math.max(0, (ttl - PROACTIVE_REFRESH_LEAD_SECONDS) * 1000);
    const timer = setTimeout(() => {
      refreshAuthToken({ clearOnFailure: false }).catch((err) => {
        Sentry.captureException(err, { tags: { area: 'match-events-ws' } });
      });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [accessToken]);

  // ── Auth-error recovery: refresh-and-retry, NEVER give up ───────────────
  useEffect(() => {
    const handleAuthError = async (err?: unknown) => {
      const now = Date.now();
      if (now - lastAuthRecoveryAt.current < AUTH_RECOVERY_COOLDOWN_MS) {
        Sentry.captureException(
          err instanceof Error ? err : new Error('match-events WS auth error'),
          { tags: { area: 'match-events-ws' } },
        );
        return;
      }
      lastAuthRecoveryAt.current = now;

      let newToken: string | null = null;
      try {
        newToken = await refreshAuthToken({ clearOnFailure: false });
      } catch (refreshErr) {
        Sentry.captureException(refreshErr, {
          tags: { area: 'match-events-ws' },
        });
      }

      if (newToken) {
        try {
          socket.disconnect();
        } catch {
          /* already closed */
        }
        socket.connect();
      } else {
        toast.error(
          'Live match events reconnecting — will recover automatically.',
          { toastId: 'match-events-ws-auth' },
        );
        Sentry.captureException(
          err instanceof Error
            ? err
            : new Error('match-events WS auth error — refresh failed'),
          { tags: { area: 'match-events-ws' } },
        );
      }
    };

    socket.on('connect_error', handleAuthError);
    socket.on('auth_error', handleAuthError);
    return () => {
      socket.off('connect_error', handleAuthError);
      socket.off('auth_error', handleAuthError);
    };
  }, [socket]);

  return {
    isConnected: socket.connected,
    frames,
    operations,
    matchState,
  };
}
